import type { StyleSpecification } from 'maplibre-gl';

import { semanticColors } from '@aztec-realms/design-tokens';
import type { DiscoveryMarkerView, ExploreViewModel } from '@aztec-realms/shared-types';

import { environmentAssetManifest } from '../game/asset-manifest';
import { worldNodeKind } from './world-node-art';

export interface CodexFeatureCollection {
  type: 'FeatureCollection';
  features: CodexFeature[];
}

interface CodexFeature {
  type: 'Feature';
  id?: string;
  properties: Record<string, boolean | number | string>;
  geometry:
    | { type: 'Point'; coordinates: number[] }
    | { type: 'LineString'; coordinates: number[][] }
    | { type: 'Polygon'; coordinates: number[][][] };
}

export type Coordinate = readonly [number, number];
type Offset = readonly [number, number];

export const FANTASY_REGIONS = [
  { code: 'jade_coast', displayName: 'Jade Coast', biome: 'garden' },
  { code: 'cloud_peaks', displayName: 'Cloud Peaks', biome: 'highland' },
  { code: 'sun_pass', displayName: 'Sun Pass', biome: 'coast' },
  { code: 'sacred_river', displayName: 'Sacred River', biome: 'wetland' },
  { code: 'obsidian_fields', displayName: 'Obsidian Fields', biome: 'volcanic' },
] as const;

export interface JourneyMapPosition {
  accuracyM: number;
  coordinates: Coordinate;
  recordedAt: string;
}

function offsetCoordinate(center: Coordinate, offset: Offset): [number, number] {
  return [center[0] + offset[0], center[1] + offset[1]];
}

function polygonFeature(
  center: Coordinate,
  offsets: readonly Offset[],
  properties: CodexFeature['properties'],
): CodexFeature {
  const coordinates = offsets.map((offset) => offsetCoordinate(center, offset));
  coordinates.push(coordinates[0] ?? [...center]);
  return {
    type: 'Feature',
    properties,
    geometry: { type: 'Polygon', coordinates: [coordinates] },
  };
}

function lineFeature(
  center: Coordinate,
  offsets: readonly Offset[],
  properties: CodexFeature['properties'],
): CodexFeature {
  return {
    type: 'Feature',
    properties,
    geometry: {
      type: 'LineString',
      coordinates: offsets.map((offset) => offsetCoordinate(center, offset)),
    },
  };
}

function knowledgeCell(discovery: DiscoveryMarkerView, index: number): CodexFeature {
  const point: Coordinate = [discovery.longitude, discovery.latitude];
  const radiusM = 54 + (index % 3) * 6;
  const latitudeRadians = (point[1] * Math.PI) / 180;
  const latitudeRadius = radiusM / 111_320;
  const longitudeRadius = radiusM / Math.max(11_132, 111_320 * Math.abs(Math.cos(latitudeRadians)));
  const offsets = Array.from({ length: 12 }, (_, segment) => {
    const angle = (Math.PI * 2 * segment) / 12;
    return [Math.cos(angle) * longitudeRadius, Math.sin(angle) * latitudeRadius] as const;
  });
  const knowledge =
    discovery.state === 'unknown' || discovery.state === 'cooldown'
      ? 'unknown'
      : discovery.state === 'nearby'
        ? 'seen'
        : 'discovered';
  return polygonFeature(point, offsets, {
    kind: 'knowledge-cell',
    knowledge,
    discoveryId: discovery.id,
    radiusM,
  });
}

/**
 * Creates deterministic local geography for the credential-free visual fixture.
 * It is presentation-only: eligibility and discovery state still come from the
 * typed server view model and are never inferred from these shapes.
 */
export function createFixtureGeography(center: Coordinate): CodexFeatureCollection {
  const mainIsland: readonly Offset[] = [
    [-0.014, -0.003],
    [-0.012, 0.006],
    [-0.007, 0.011],
    [0.001, 0.0125],
    [0.009, 0.009],
    [0.014, 0.003],
    [0.011, -0.006],
    [0.005, -0.011],
    [-0.004, -0.012],
    [-0.011, -0.008],
  ];
  const northIsland: readonly Offset[] = [
    [0.008, 0.012],
    [0.014, 0.014],
    [0.019, 0.01],
    [0.018, 0.004],
    [0.013, 0.002],
    [0.009, 0.006],
  ];
  const westIsland: readonly Offset[] = [
    [-0.022, 0.008],
    [-0.017, 0.012],
    [-0.012, 0.009],
    [-0.013, 0.002],
    [-0.019, 0.001],
  ];
  const southIsland: readonly Offset[] = [
    [-0.004, -0.014],
    [0.004, -0.012],
    [0.011, -0.015],
    [0.009, -0.02],
    [0.001, -0.021],
    [-0.006, -0.018],
  ];
  const eastIsland: readonly Offset[] = [
    [0.017, -0.006],
    [0.024, -0.003],
    [0.027, -0.009],
    [0.023, -0.015],
    [0.016, -0.013],
    [0.013, -0.008],
  ];
  const gardenOffsets: readonly (readonly Offset[])[] = [
    [
      [-0.01, -0.006],
      [-0.0055, -0.006],
      [-0.0048, -0.0032],
      [-0.0094, -0.003],
    ],
    [
      [-0.002, 0.004],
      [0.003, 0.0048],
      [0.0038, 0.008],
      [-0.0012, 0.0072],
    ],
    [
      [0.0045, -0.0065],
      [0.0093, -0.0048],
      [0.0082, -0.0018],
      [0.0037, -0.003],
    ],
  ];
  const terraceOffsets: readonly (readonly Offset[])[] = [
    [
      [-0.0055, -0.0007],
      [-0.002, -0.0007],
      [-0.0015, 0.0014],
      [-0.005, 0.0016],
    ],
    [
      [0.001, 0.0005],
      [0.0053, 0.001],
      [0.005, 0.0033],
      [0.0008, 0.003],
    ],
    [
      [-0.0038, 0.0032],
      [-0.0008, 0.0035],
      [-0.001, 0.006],
      [-0.0042, 0.0056],
    ],
  ];

  return {
    type: 'FeatureCollection',
    features: [
      polygonFeature(center, mainIsland, { kind: 'land', biome: 'garden', region: 'Jade Coast' }),
      polygonFeature(center, northIsland, {
        kind: 'land',
        biome: 'highland',
        region: 'Cloud Peaks',
      }),
      polygonFeature(center, westIsland, { kind: 'land', biome: 'coast', region: 'Sun Pass' }),
      polygonFeature(center, southIsland, {
        kind: 'land',
        biome: 'wetland',
        region: 'Sacred River',
      }),
      polygonFeature(center, eastIsland, {
        kind: 'land',
        biome: 'volcanic',
        region: 'Obsidian Fields',
      }),
      polygonFeature(
        center,
        [
          [0.008, 0.006],
          [0.014, 0.0065],
          [0.016, 0.011],
          [0.01, 0.012],
        ],
        { kind: 'highland' },
      ),
      ...gardenOffsets.map((offsets, index) =>
        polygonFeature(center, offsets, { kind: 'garden', discovered: index < 2 }),
      ),
      ...terraceOffsets.map((offsets) => polygonFeature(center, offsets, { kind: 'terrace' })),
      lineFeature(
        center,
        [
          [-0.012, 0.002],
          [-0.006, 0.001],
          [-0.001, -0.001],
          [0.004, -0.003],
          [0.011, -0.005],
        ],
        { kind: 'canal' },
      ),
      lineFeature(
        center,
        [
          [-0.009, -0.008],
          [-0.004, -0.004],
          [0, 0],
          [0.006, 0.004],
          [0.013, 0.007],
        ],
        { kind: 'causeway' },
      ),
      lineFeature(
        center,
        [
          [-0.019, 0.005],
          [-0.014, 0.004],
          [-0.01, 0.002],
        ],
        { kind: 'causeway' },
      ),
      lineFeature(
        center,
        [
          [-0.006, -0.014],
          [-0.003, -0.01],
          [-0.002, -0.006],
        ],
        { kind: 'causeway' },
      ),
      lineFeature(
        center,
        [
          [0.0105, 0.007],
          [0.012, 0.0085],
          [0.013, 0.0105],
          [0.0115, 0.012],
        ],
        { kind: 'contour' },
      ),
    ],
  };
}

export function createCodexMapStyle(center: Coordinate): StyleSpecification {
  const geography = createFixtureGeography(center);
  const atlasCoordinates: [[number, number], [number, number], [number, number], [number, number]] =
    [
      offsetCoordinate(center, [-0.054, 0.03]),
      offsetCoordinate(center, [0.054, 0.03]),
      offsetCoordinate(center, [0.054, -0.03]),
      offsetCoordinate(center, [-0.054, -0.03]),
    ];
  return {
    version: 8,
    name: 'Aztec Realms illustrated Codex map',
    sources: {
      'codex-illustration': {
        type: 'image',
        url: environmentAssetManifest.explore.url,
        coordinates: atlasCoordinates,
      },
      'codex-geography': { type: 'geojson', data: geography },
    },
    layers: [
      {
        id: 'codex-water',
        type: 'background',
        paint: { 'background-color': '#174f5d' },
      },
      {
        id: 'illustrated-world-atlas',
        source: 'codex-illustration',
        type: 'raster',
        paint: {
          'raster-opacity': 0.94,
          'raster-saturation': 0.08,
          'raster-contrast': 0.1,
          'raster-fade-duration': 0,
        },
      },
      {
        id: 'island-shadow',
        source: 'codex-geography',
        type: 'fill',
        filter: ['==', ['get', 'kind'], 'land'],
        paint: {
          'fill-color': semanticColors.backgroundDeep,
          'fill-opacity': 0.22,
          'fill-translate': [3, 6],
        },
      },
      {
        id: 'island-parchment',
        source: 'codex-geography',
        type: 'fill',
        filter: ['==', ['get', 'kind'], 'land'],
        paint: {
          'fill-color': [
            'match',
            ['get', 'biome'],
            'highland',
            '#867e66',
            'coast',
            '#a99b68',
            'wetland',
            '#557b62',
            'volcanic',
            '#594b49',
            '#66895f',
          ],
          'fill-opacity': 0.16,
          'fill-outline-color': '#d8c7a4',
        },
      },
      {
        id: 'highland-relief',
        source: 'codex-geography',
        type: 'fill',
        filter: ['==', ['get', 'kind'], 'highland'],
        paint: {
          'fill-color': '#625e54',
          'fill-opacity': 0.3,
          'fill-outline-color': semanticColors.sunGold,
        },
      },
      {
        id: 'garden-pigment',
        source: 'codex-geography',
        type: 'fill',
        filter: ['==', ['get', 'kind'], 'garden'],
        paint: {
          'fill-color': ['case', ['get', 'discovered'], '#3e8b61', '#a79873'],
          'fill-opacity': ['case', ['get', 'discovered'], 0.34, 0.2],
          'fill-outline-color': '#e3c468',
        },
      },
      {
        id: 'terrace-stone',
        source: 'codex-geography',
        type: 'fill',
        filter: ['==', ['get', 'kind'], 'terrace'],
        paint: {
          'fill-color': '#d4c393',
          'fill-opacity': 0.36,
          'fill-outline-color': '#8e6b31',
        },
      },
      {
        id: 'water-canal-shadow',
        source: 'codex-geography',
        type: 'line',
        filter: ['==', ['get', 'kind'], 'canal'],
        paint: {
          'line-color': semanticColors.backgroundDeep,
          'line-width': 8,
          'line-opacity': 0.5,
        },
      },
      {
        id: 'living-water-canals',
        source: 'codex-geography',
        type: 'line',
        filter: ['==', ['get', 'kind'], 'canal'],
        paint: { 'line-color': '#35b9b0', 'line-width': 4, 'line-opacity': 0.82 },
      },
      {
        id: 'causeway-shadow',
        source: 'codex-geography',
        type: 'line',
        filter: ['==', ['get', 'kind'], 'causeway'],
        paint: { 'line-color': '#192a27', 'line-width': 7, 'line-opacity': 0.52 },
      },
      {
        id: 'sandstone-causeways',
        source: 'codex-geography',
        type: 'line',
        filter: ['==', ['get', 'kind'], 'causeway'],
        paint: { 'line-color': '#ead9ad', 'line-width': 3.5, 'line-opacity': 0.92 },
      },
      {
        id: 'codex-contours',
        source: 'codex-geography',
        type: 'line',
        filter: ['==', ['get', 'kind'], 'contour'],
        paint: {
          'line-color': semanticColors.sunGold,
          'line-width': 1.5,
          'line-opacity': 0.72,
          'line-dasharray': [1, 1.4],
        },
      },
    ],
  };
}

export function markerIconKey(discovery: DiscoveryMarkerView): string {
  // Tier and rarity live in labels, not this sprite: reuse the atlas across every public stop.
  return `codex-marker-${worldNodeKind(discovery.nodeKind ?? discovery.kind)}-${discovery.state}`;
}

export function createExploreOverlay(
  snapshot: ExploreViewModel,
  currentPosition?: JourneyMapPosition,
  activeRoute: readonly JourneyMapPosition[] = [],
  journeyActive = snapshot.journeyActive,
): CodexFeatureCollection {
  const routeCoordinates = journeyActive
    ? activeRoute.map((position) => [...position.coordinates])
    : [];
  const routeSteps = routeCoordinates.slice(0, -1).flatMap((start, routeIndex) => {
    const end = routeCoordinates[routeIndex + 1];
    if (!end) return [];
    return [0.34, 0.68].map((progress, stepIndex) => ({
      type: 'Feature' as const,
      properties: {
        kind: 'route-step',
        sequence: routeIndex * 2 + stepIndex + 1,
        major: stepIndex === 1,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [
          (start[0] ?? snapshot.center[0]) +
            ((end[0] ?? snapshot.center[0]) - (start[0] ?? snapshot.center[0])) * progress,
          (start[1] ?? snapshot.center[1]) +
            ((end[1] ?? snapshot.center[1]) - (start[1] ?? snapshot.center[1])) * progress,
        ],
      },
    }));
  });
  const worldRoutes = (snapshot.routeContracts ?? []).flatMap((route) => [
    {
      type: 'Feature' as const,
      id: route.id,
      properties: {
        kind: 'world-route',
        routeId: route.id,
        state: route.state,
        family: route.familyCode,
      },
      geometry: {
        type: 'LineString' as const,
        coordinates: route.stops.map((stop) => [...stop.coordinates]),
      },
    },
    ...route.stops.map((stop) => ({
      type: 'Feature' as const,
      properties: {
        kind: 'world-route-stop',
        routeId: route.id,
        routeState: route.state,
        stopState: stop.state,
        sequence: stop.sequence,
      },
      geometry: { type: 'Point' as const, coordinates: [...stop.coordinates] },
    })),
  ]);
  return {
    type: 'FeatureCollection',
    features: [
      ...snapshot.discoveries.map(knowledgeCell),
      ...(snapshot.energyLinks ?? [])
        .filter((link) => link.state === 'awakened')
        .map((link) => ({
          type: 'Feature' as const,
          id: link.id,
          properties: {
            kind: 'energy-link',
            state: link.state,
            family: link.familyCode,
            requiredTier: link.requiredTier,
          },
          geometry: {
            type: 'LineString' as const,
            coordinates: [[...link.sourceCoordinates], [...link.destinationCoordinates]],
          },
        })),
      ...(snapshot.weaves ?? []).map((weave) => ({
        type: 'Feature' as const,
        id: weave.id,
        properties: {
          kind: 'energy-link',
          state: weave.status === 'awakened' ? 'awakened' : 'ready',
          family: weave.familyCode,
          requiredTier: 1,
          playerSelected: true,
        },
        geometry: {
          type: 'LineString' as const,
          coordinates: weave.routeCoordinates.map((coordinate) => [...coordinate]),
        },
      })),
      ...worldRoutes,
      ...(routeCoordinates.length >= 2
        ? [
            {
              type: 'Feature' as const,
              properties: { kind: 'active-route', active: true },
              geometry: { type: 'LineString' as const, coordinates: routeCoordinates },
            },
          ]
        : []),
      ...routeSteps,
      ...(currentPosition
        ? [
            {
              type: 'Feature' as const,
              properties: {
                kind: 'interaction-radius',
                radiusM: snapshot.interactionRadiusM,
                active: journeyActive,
              },
              geometry: {
                type: 'Polygon' as const,
                coordinates: [
                  interactionRadiusCoordinates(
                    currentPosition.coordinates,
                    snapshot.interactionRadiusM,
                  ),
                ],
              },
            },
            {
              type: 'Feature' as const,
              properties: {
                kind: 'current-position',
                accuracyM: currentPosition.accuracyM,
                active: journeyActive,
              },
              geometry: {
                type: 'Point' as const,
                coordinates: [...currentPosition.coordinates],
              },
            },
          ]
        : []),
    ],
  };
}

function interactionRadiusCoordinates(
  center: readonly [number, number],
  interactionRadiusM: number,
): [number, number][] {
  const [longitude, latitude] = center;
  const latitudeRadians = (latitude * Math.PI) / 180;
  const latitudeDelta = interactionRadiusM / 111_320;
  const longitudeDelta =
    interactionRadiusM / Math.max(11_132, 111_320 * Math.abs(Math.cos(latitudeRadians)));
  return Array.from({ length: 49 }, (_, index) => {
    const angle = (Math.PI * 2 * index) / 48;
    return [
      longitude + Math.cos(angle) * longitudeDelta,
      latitude + Math.sin(angle) * latitudeDelta,
    ];
  });
}

export function createDiscoveryPoints(
  snapshot: ExploreViewModel,
  revealDiscoveryId?: string,
): CodexFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: snapshot.discoveries.map((discovery) => ({
      type: 'Feature',
      id: discovery.id,
      properties: {
        id: discovery.id,
        state: discovery.state,
        rarity: discovery.rarity,
        kind: discovery.kind,
        nodeKind: discovery.nodeKind ?? '',
        nodeTier: discovery.nodeTier ?? 0,
        attunementLevel: discovery.attunementLevel ?? 0,
        weaveRole: discovery.weaveRole ?? '',
        icon: markerIconKey(discovery),
        reveal: discovery.id === revealDiscoveryId,
      },
      geometry: { type: 'Point', coordinates: [discovery.longitude, discovery.latitude] },
    })),
  };
}

function markerAccent(state: DiscoveryMarkerView['state']): string {
  if (state === 'eligible') return '#ffd36a';
  if (state === 'queued') return '#35c6b5';
  if (state === 'claimed') return '#1aa68c';
  if (state === 'cooldown' || state === 'unknown') return '#a99f88';
  return '#e9cf8c';
}

function markerMotif(discovery: DiscoveryMarkerView, accent: string): string {
  switch (discovery.nodeKind) {
    case 'glyph_archive':
      return `<path d="M19 18h26v29H19zM24 13h16v5H24zm2 12h12M26 31h12M26 37h8" fill="none" stroke="${accent}" stroke-width="3" stroke-linejoin="round"/>`;
    case 'civic_nexus':
      return `<path d="M15 45h34M19 41h26l-4-8H23zm7-12h12l-3-8h-6zM32 13v8" fill="none" stroke="${accent}" stroke-width="3" stroke-linejoin="round"/>`;
    case 'verdant_waypoint':
      return `<path d="M32 47V29m0 7c-11 0-15-8-12-14 8 0 12 5 12 14Zm0-5c10 0 14-7 12-13-8 0-12 5-12 13Z" fill="none" stroke="${accent}" stroke-width="3" stroke-linejoin="round"/>`;
    case 'water_gate':
      return `<path d="M17 30h30v17H17zm6 0V19h18v11M14 24h36M22 40c3-4 6-4 10 0 4-4 7-4 10 0" fill="none" stroke="${accent}" stroke-width="3"/>`;
    case 'celestial_observatory':
      return `<circle cx="32" cy="28" r="10" fill="none" stroke="${accent}" stroke-width="3"/><path d="M32 12v5m0 22v6M16 28h5m22 0h5M22 18l-4-4m24 4 4-4M20 48h24" fill="none" stroke="${accent}" stroke-width="3"/>`;
    case 'market_exchange':
      return `<path d="M17 27h30l-4-10H21zm3 0v20h24V27M25 47V35h14v12" fill="none" stroke="${accent}" stroke-width="3" stroke-linejoin="round"/>`;
  }
  switch (discovery.kind) {
    case 'glyph_fragment':
      return `<path d="M32 15 37 27 49 32 37 37 32 49 27 37 15 32 27 27Z" fill="${accent}"/><circle cx="32" cy="32" r="7" fill="#0e1d1c" stroke="${accent}" stroke-width="2"/>`;
    case 'ruin':
      return `<path d="M17 44h30M20 40h24l-4-7h-16Zm6-11h12l-3-7h-6Zm5-11h2" fill="none" stroke="${accent}" stroke-width="4" stroke-linejoin="round"/>`;
    case 'cache':
      return `<path d="M32 18 46 27 42 45H22L18 27Zm-14 9h28M32 18v27" fill="none" stroke="${accent}" stroke-width="3"/>`;
    case 'celestial':
      return `<circle cx="32" cy="32" r="12" fill="none" stroke="${accent}" stroke-width="3"/><path d="M36 21a12 12 0 0 0 0 22 11 11 0 1 1 0-22Z" fill="${accent}"/>`;
    case 'tribe':
      return `<path d="M23 47V18m2 2h19l-6 8 6 8H25" fill="none" stroke="${accent}" stroke-width="4" stroke-linejoin="round"/><circle cx="23" cy="48" r="3" fill="${accent}"/>`;
    case 'seasonal':
      return `<path d="M32 31c-14-2-14-14-5-14 5 0 6 7 5 14Zm0 0c2-14 14-14 14-5 0 5-7 6-14 5Zm0 0c14 2 14 14 5 14-5 0-6-7-5-14Zm0 0c-2 14-14 14-14 5 0-5 7-6 14-5Z" fill="${accent}"/>`;
  }
}

/** A replaceable, CSP-safe data image used by MapLibre's sprite layer. */
export function createDiscoveryMarkerDataUrl(discovery: DiscoveryMarkerView): string {
  const accent = markerAccent(discovery.state);
  const rareRing =
    discovery.rarity === 'rare' || discovery.rarity === 'legendary'
      ? `<circle cx="32" cy="32" r="28" fill="none" stroke="#ffd36a" stroke-width="2" stroke-dasharray="3 3"/>`
      : '';
  const queuedRing =
    discovery.state === 'queued'
      ? `<circle cx="32" cy="32" r="23" fill="none" stroke="#35c6b5" stroke-width="2" stroke-dasharray="2 3"/>`
      : '';
  const claimedMark =
    discovery.state === 'claimed'
      ? `<path d="m42 44 4 4 8-10" fill="none" stroke="#ffd36a" stroke-width="3" stroke-linecap="round"/>`
      : '';
  const tierMarks = discovery.nodeTier
    ? Array.from(
        { length: discovery.nodeTier },
        (_, index) =>
          `<circle cx="${24 + index * 4}" cy="54" r="1.4" fill="#ffd36a" stroke="#0e1d1c" stroke-width=".6"/>`,
      ).join('')
    : '';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><circle cx="32" cy="32" r="25" fill="#0e1d1c" fill-opacity=".94" stroke="#d9a83e" stroke-width="2"/><circle cx="32" cy="32" r="20" fill="#123a39" stroke="${accent}" stroke-width="2"/>${rareRing}${queuedRing}${markerMotif(discovery, accent)}${tierMarks}${claimedMark}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
